import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface Plan {
  id: string; name: string; description: string | null; price_monthly: number;
  max_users: number; max_contracts: number; storage_mb: number; is_active: boolean;
}

const PLAN_STYLE: Record<string, { accent: string; border: string; badge: string }> = {
  free:       { accent: 'text-slate-500',   border: 'border-slate-200',    badge: 'bg-slate-100 text-slate-500' },
  pro:        { accent: 'text-amber-700',   border: 'border-amber-200',    badge: 'bg-amber-50 text-amber-700' },
  enterprise: { accent: 'text-emerald-700', border: 'border-emerald-200',  badge: 'bg-emerald-50 text-emerald-700' },
};

const storageLabel = (mb: number) => mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB` : `${mb} MB`;

export default function PlansTab() {
  const [plans, setPlans]     = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('saas_plans').select('*').order('price_monthly')
      .then(({ data }) => { setPlans((data ?? []) as Plan[]); setLoading(false); });
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const s = PLAN_STYLE[plan.id] ?? PLAN_STYLE.free;
          return (
            <div key={plan.id} className={`bg-white rounded-xl p-5 border ${s.border}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.badge}`}>{plan.name}</span>
                  <p className={`text-2xl font-bold mt-2 ${s.accent}`}>
                    {plan.price_monthly === 0 ? 'Free' : `$${plan.price_monthly}`}
                    {plan.price_monthly > 0 && <span className="text-slate-400 text-sm font-normal">/mes</span>}
                  </p>
                </div>
                <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.badge}`}>
                  <i className="ri-vip-crown-line text-sm"></i>
                </span>
              </div>
              {plan.description && <p className="text-slate-500 text-xs mb-4 leading-relaxed">{plan.description}</p>}
              <div className="space-y-2 pt-3 border-t border-slate-100">
                {[
                  { label: 'Usuarios',  value: plan.max_users >= 999 ? 'Ilimitado' : `${plan.max_users}` },
                  { label: 'Contratos', value: plan.max_contracts >= 9999 ? 'Ilimitado' : `${plan.max_contracts}` },
                  { label: 'Storage',   value: storageLabel(plan.storage_mb) },
                ].map((lim) => (
                  <div key={lim.label} className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">{lim.label}</span>
                    <span className={`font-semibold ${s.accent}`}>{lim.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100">
                <span className={`text-[10px] font-medium ${plan.is_active ? 'text-emerald-600' : 'text-red-500'}`}>
                  <i className={`${plan.is_active ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} mr-1`}></i>
                  {plan.is_active ? 'Plan activo' : 'Plan inactivo'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-slate-500 text-xs leading-relaxed">
          <i className="ri-information-line mr-1.5 text-slate-400"></i>
          Para modificar los límites de cada plan, edita directamente los registros en la tabla <span className="font-mono text-slate-700">saas_plans</span> desde Supabase Dashboard.
        </p>
      </div>
    </div>
  );
}
